<script lang="ts">
  import { createContext } from 'reka-ui'
  import { ref, type Ref } from 'vue'
  import { type GatButtonProps } from '@gat-ship/vue-components'

  export interface ButtonDropdownProps extends GatButtonProps {
    open?: boolean
    align?: 'start' | 'center' | 'end' | undefined
    side?: 'top' | 'bottom' | 'left' | 'right' | undefined
  }

  export type ButtonDropdownContext = {
    open: Ref<boolean>
    keepOpenOnClick: Ref<boolean>
  }

  const [_useButtonDropdown, provideButtonDropdown] = createContext<{
    open: Ref<boolean>
    keepOpenOnClick: Ref<boolean>
  }>('gs-button-dropdown')

  export function useButtonDropdown(): ButtonDropdownContext {
    try {
      return _useButtonDropdown()
    } catch (error) {
      return {
        open: ref(false),
        keepOpenOnClick: ref(true)
      }
    }
  }
</script>

<script setup lang="ts">
  import { GenericButtonProvider } from '../generic-button'
  import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent
  } from '@gat-ship/vue-components/dropdown-menu'

  defineOptions({
    inheritAttrs: false
  })

  const props = withDefaults(defineProps<ButtonDropdownProps>(), {
    align: 'end',
    side: 'bottom'
  })

  const open = defineModel<boolean>('open', { default: false })
  const keepOpenOnClick = defineModel<boolean>('keepOpenOnClick', {
    default: false
  })

  function updateOpen(openState: boolean) {
    if (!keepOpenOnClick.value) {
      open.value = openState
    }
  }

  provideButtonDropdown({
    open,
    keepOpenOnClick
  })
</script>

<template>
  <GenericButtonProvider is-of-parent-type="dropdownMenu">
    <DropdownMenu :open="open" @update:open="updateOpen">
      <DropdownMenuTrigger as-child>
        <slot name="trigger" v-bind="props">
          <gat-button v-bind="props" />
        </slot>
      </DropdownMenuTrigger>
      <DropdownMenuContent :align="props.align" :side="props.side">
        <slot />
      </DropdownMenuContent>
    </DropdownMenu>
  </GenericButtonProvider>
</template>
